import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Divider,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import PageLayout from "../../components/layout/PageLayout";
import { useGetPaymentErrorByIdQuery } from "../../store/api";
import { toast } from "react-toastify";

import {
  // getInvoiceCustomId,
  getLeadName,
  getLeadEmail,
  getErrorMessage,
  formatCreatedAt,
} from "./paymentErrorRowMappers";

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const ViewPaymentError = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetPaymentErrorByIdQuery(id, { skip: !id });

  const row = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || "Failed to load payment error");
      navigate("/payment-errors");
    }
  }, [isError, error, navigate]);

  if (loading || !row) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const errorMessage = getErrorMessage(row);

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate("/payment-errors")}
          >
            Back
          </Button>

          <Chip
            icon={<BugReportOutlinedIcon />}
            label="Payment Error"
            color="error"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 5,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(211,47,47,0.08), rgba(211,47,47,0.02))",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Payment Error Details
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Incident log and debugging information
          </Typography>
        </Paper>

        {/* Main Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Metadata */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  {/* <InfoItem
                    label="Invoice Custom ID"
                    value={getInvoiceCustomId(row)}
                  /> */}

                  <InfoItem label="Lead" value={getLeadName(row)} />

                  <InfoItem label="Lead email" value={getLeadEmail(row)} />

                  <InfoItem label="Created At" value={formatCreatedAt(row)} />
                </Stack>
              </Grid>

              {/* Error block */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: "#fff5f5",
                    borderColor: "error.light",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Error Message
                    </Typography>

                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(errorMessage);
                        toast.success("Copied to clipboard");
                      }}
                    >
                      Copy
                    </Button>
                  </Stack>

                  <Divider sx={{ mb: 1.5 }} />

                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                      lineHeight: 1.6,
                      color: "error.dark",
                    }}
                  >
                    {errorMessage}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewPaymentError;
